import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wand2, Upload, Play, Loader2, Check, X, ChevronRight, 
  Sparkles, Film, RefreshCw, Clock, Download, AlertCircle,
  Cake, Heart, GraduationCap, Baby, PartyPopper, Briefcase,
  MessageSquare, Calendar, BookOpen, Smartphone, Instagram, Youtube,
  Plane, Dumbbell, UtensilsCrossed, PawPrint, Clapperboard, 
  Camera, Stars, Theater
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const categoryIcons = {
  celebrations: PartyPopper,
  business: Briefcase,
  social: Smartphone,
  lifestyle: Stars,
  creative: Clapperboard
};

// Template icons mapping
const templateIcons = {
  birthday: Cake,
  wedding: Heart,
  graduation: GraduationCap,
  baby_shower: Baby,
  anniversary: Heart,
  new_year: PartyPopper,
  product_demo: Briefcase,
  testimonial: MessageSquare,
  event_recap: Calendar,
  tutorial: BookOpen,
  tiktok_viral: Smartphone,
  instagram_aesthetic: Instagram,
  youtube_highlight: Youtube,
  travel_vlog: Plane,
  fitness: Dumbbell,
  food_cooking: UtensilsCrossed,
  pet_moments: PawPrint,
  cinematic: Clapperboard,
  vintage_retro: Camera,
  dreamy_fantasy: Stars,
  noir_dramatic: Theater
};

// Intensity descriptions
const intensityDescriptions = {
  subtle: "Gentle enhancements that preserve the original feel",
  moderate: "Balanced transformations with noticeable improvements",
  dramatic: "Bold, striking changes with maximum visual impact"
};

export default function AIVideoEditor({ 
  videoUrl, 
  videoName,
  videoFile, // Optional: the actual File object if video was uploaded locally
  onComplete,
  onClose 
}) {
  const [categories, setCategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [intensity, setIntensity] = useState('moderate');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [currentJob, setCurrentJob] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [step, setStep] = useState(1); // 1: Select template, 2: Customize, 3: Processing
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const getAuthToken = () => localStorage.getItem('auth_token');

  // Check if the video URL is a blob URL (local file)
  const isBlobUrl = (url) => {
    return url && (url.startsWith('blob:') || url.startsWith('data:'));
  };

  // Upload local video file to S3 for AI processing
  const uploadVideoForAI = async (file) => {
    const formData = new FormData();
    formData.append('video', file);

    const response = await fetch(`${API_URL}/api/video-studio/upload-for-ai`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload video');
    }

    return await response.json();
  };

  // Fetch templates on mount
  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [pollInterval]);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await fetch(`${API_URL}/api/video-studio/ai-editor/templates`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch templates');
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomPrompt(''); // Reset custom prompt when selecting a template
  };

  const handleStartEdit = async () => {
    if (!videoUrl) {
      toast.error('No video selected');
      return;
    }

    if (!selectedTemplate && !customPrompt.trim()) {
      toast.error('Please select a template or enter a custom prompt');
      return;
    }

    try {
      setLoading(true);
      setStep(3);

      let finalVideoUrl = videoUrl;

      // If the video is a blob URL (local file), we need to upload it first
      if (isBlobUrl(videoUrl)) {
        if (!videoFile) {
          // Try to fetch the blob and create a file from it
          try {
            setIsUploading(true);
            setUploadProgress(10);
            toast.info('Uploading video for AI processing...', { duration: 3000 });
            
            const response = await fetch(videoUrl);
            const blob = await response.blob();
            const file = new File([blob], videoName || 'video.mp4', { type: blob.type || 'video/mp4' });
            
            setUploadProgress(40);
            const uploadResult = await uploadVideoForAI(file);
            setUploadProgress(100);
            
            finalVideoUrl = uploadResult.url;
            toast.success('Video uploaded successfully!');
          } catch (uploadError) {
            console.error('Error uploading blob video:', uploadError);
            throw new Error('Failed to upload video. Please try selecting your video from the library instead.');
          } finally {
            setIsUploading(false);
          }
        } else {
          // We have the file object, upload it directly
          try {
            setIsUploading(true);
            setUploadProgress(10);
            toast.info('Uploading video for AI processing...', { duration: 3000 });
            
            setUploadProgress(30);
            const uploadResult = await uploadVideoForAI(videoFile);
            setUploadProgress(100);
            
            finalVideoUrl = uploadResult.url;
            toast.success('Video uploaded successfully!');
          } catch (uploadError) {
            console.error('Error uploading video file:', uploadError);
            throw new Error('Failed to upload video. Please try again.');
          } finally {
            setIsUploading(false);
          }
        }
      }

      const requestBody = {
        video_url: finalVideoUrl,
        prompt: customPrompt.trim() || `Transform this video to be more engaging and professional.`,
        template_id: selectedTemplate?.id || null,
        intensity: intensity
      };

      const response = await fetch(`${API_URL}/api/video-studio/ai-editor/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start AI edit');
      }

      const data = await response.json();
      setCurrentJob({ id: data.job_id, status: 'processing', progress: 0 });
      
      // Start polling for job status
      startPolling(data.job_id);
      
      toast.success('AI video edit started!');
    } catch (error) {
      console.error('Error starting AI edit:', error);
      toast.error(error.message || 'Failed to start AI edit');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/video-studio/ai-editor/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch job status');
        
        const job = await response.json();
        setCurrentJob(job);

        if (job.status === 'completed') {
          clearInterval(interval);
          setPollInterval(null);
          toast.success('AI video edit complete!');
          if (onComplete) {
            onComplete({
              url: job.output_video_url,
              name: job.output_name || `AI Edited - ${videoName}`
            });
          }
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setPollInterval(null);
          toast.error(`Edit failed: ${job.error || 'Unknown error'}`);
          setStep(2);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    }, 3000); // Poll every 3 seconds

    setPollInterval(interval);
  };

  const renderTemplateGrid = () => (
    <div className="space-y-6">
      {loadingTemplates ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      ) : (
        categories.map((category) => {
          const CategoryIcon = categoryIcons[category.id] || Sparkles;
          return (
            <div key={category.id} className="space-y-3">
              <div className="flex items-center gap-2">
                <CategoryIcon className="w-5 h-5 text-purple-400" />
                <h3 className="text-sm font-semibold text-white">{category.name}</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {category.templates.map((template) => {
                  const TemplateIcon = templateIcons[template.id] || Sparkles;
                  const isSelected = selectedTemplate?.id === template.id;
                  return (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`relative p-3 rounded-xl border transition-all text-left ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-500/20 ring-2 ring-purple-500/50' 
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-800'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-4 h-4 text-purple-400" />
                        </div>
                      )}
                      <div className="text-2xl mb-2">{template.icon}</div>
                      <div className="text-sm font-medium text-white truncate">{template.name}</div>
                      <div className="text-xs text-zinc-400 mt-1 truncate">{template.music_mood}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
      
      {/* Custom option */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-cyan-400" />
          <h3 className="text-sm font-semibold text-white">Custom Edit</h3>
        </div>
        <button
          onClick={() => {
            setSelectedTemplate(null);
            setStep(2);
          }}
          className={`w-full p-4 rounded-xl border border-dashed transition-all text-center ${
            !selectedTemplate 
              ? 'border-cyan-500 bg-cyan-500/10' 
              : 'border-zinc-600 bg-zinc-800/30 hover:border-cyan-500/50'
          }`}
        >
          <Sparkles className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-white">Describe your own transformation</div>
          <div className="text-xs text-zinc-400 mt-1">Tell AI exactly how to edit your video</div>
        </button>
      </div>
    </div>
  );

  const renderCustomizeStep = () => (
    <div className="space-y-6">
      {/* Selected template display */}
      {selectedTemplate && (
        <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{selectedTemplate.icon}</div>
            <div>
              <div className="font-semibold text-white">{selectedTemplate.name}</div>
              <div className="text-sm text-zinc-400">{selectedTemplate.music_mood}</div>
            </div>
            <button 
              onClick={() => { setSelectedTemplate(null); setStep(1); }}
              className="ml-auto p-1 text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Custom prompt */}
      <div className="space-y-2">
        <Label className="text-white">
          {selectedTemplate ? 'Additional Instructions (Optional)' : 'Describe Your Transformation'}
        </Label>
        <Textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder={selectedTemplate 
            ? "Add any specific details... e.g., 'Include text overlay saying Happy Birthday John'" 
            : "Describe how you want your video transformed... e.g., 'Make it look like a professional wedding video with romantic lighting and elegant transitions'"
          }
          className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
        />
      </div>

      {/* Intensity slider */}
      <div className="space-y-3">
        <Label className="text-white">Effect Intensity</Label>
        <div className="flex gap-2">
          {['subtle', 'moderate', 'dramatic'].map((level) => (
            <button
              key={level}
              onClick={() => setIntensity(level)}
              className={`flex-1 py-2 px-3 rounded-lg border transition-all ${
                intensity === level
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <div className="text-sm font-medium capitalize">{level}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-zinc-500">{intensityDescriptions[intensity]}</p>
      </div>

      {/* Start button */}
      <Button
        onClick={handleStartEdit}
        disabled={loading || (!selectedTemplate && !customPrompt.trim())}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <Wand2 className="w-4 h-4 mr-2" />
            Transform Video with AI
          </>
        )}
      </Button>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center py-8">
      <div className="relative w-24 h-24 mx-auto">
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse opacity-30" />
        <div className="absolute inset-2 rounded-full bg-zinc-900 flex items-center justify-center">
          {currentJob?.status === 'completed' ? (
            <Check className="w-10 h-10 text-green-500" />
          ) : currentJob?.status === 'failed' ? (
            <AlertCircle className="w-10 h-10 text-red-500" />
          ) : isUploading ? (
            <Upload className="w-10 h-10 text-blue-400 animate-pulse" />
          ) : (
            <Wand2 className="w-10 h-10 text-purple-400 animate-pulse" />
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-2">
          {isUploading
            ? 'Uploading Video...'
            : currentJob?.status === 'completed' 
            ? (currentJob?.is_highlight_reel ? 'Highlight Reel Created!' : 'Transformation Complete!')
            : currentJob?.status === 'failed'
            ? 'Transformation Failed'
            : (currentJob?.is_highlight_reel 
              ? 'Creating Your Highlight Reel' 
              : 'AI is Transforming Your Video')
          }
        </h3>
        <p className="text-sm text-zinc-400">
          {isUploading 
            ? 'Preparing your video for AI processing...'
            : currentJob?.progress_message || 'Please wait, this may take 2-5 minutes...'}
        </p>
        {currentJob?.is_highlight_reel && currentJob?.status !== 'completed' && (
          <p className="text-xs text-purple-400 mt-2">
            Extracting key moments and transforming each clip...
          </p>
        )}
      </div>

      {/* Upload progress bar */}
      {isUploading && (
        <div className="space-y-2 max-w-xs mx-auto">
          <Progress value={uploadProgress} className="h-2" />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{uploadProgress}%</span>
            <span className="flex items-center gap-1">
              <Upload className="w-3 h-3" />
              Uploading
            </span>
          </div>
        </div>
      )}

      {/* AI Processing progress bar */}
      {!isUploading && currentJob && currentJob.status !== 'failed' && (
        <div className="space-y-2 max-w-xs mx-auto">
          <Progress value={currentJob.progress || 0} className="h-2" />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{currentJob.progress || 0}%</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Processing
            </span>
          </div>
        </div>
      )}

      {currentJob?.status === 'completed' && currentJob.output_video_url && (
        <div className="space-y-4">
          {/* Highlight reel info */}
          {currentJob?.is_highlight_reel && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-4 text-sm">
                <div>
                  <span className="text-zinc-400">Original:</span>
                  <span className="text-white ml-1">{Math.round(currentJob.original_duration || 0)}s</span>
                </div>
                <span className="text-zinc-600">→</span>
                <div>
                  <span className="text-zinc-400">Highlight:</span>
                  <span className="text-purple-400 ml-1">{Math.round(currentJob.highlight_duration || 0)}s</span>
                </div>
                <div>
                  <span className="text-zinc-400">Clips:</span>
                  <span className="text-white ml-1">{currentJob.clips_used || 0}</span>
                </div>
              </div>
            </div>
          )}
          
          <video 
            src={currentJob.output_video_url} 
            controls 
            className="w-full max-w-md mx-auto rounded-lg"
          />
          <div className="flex gap-2 justify-center">
            <Button 
              variant="outline" 
              onClick={() => window.open(currentJob.output_video_url, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={onClose}>
              <Check className="w-4 h-4 mr-2" />
              Done
            </Button>
          </div>
        </div>
      )}

      {currentJob?.status === 'failed' && (
        <Button variant="outline" onClick={() => setStep(2)}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-400" />
            AI Video Editor
          </h2>
          <p className="text-sm text-zinc-400">Transform your video with AI magic</p>
        </div>
        {step > 1 && step < 3 && (
          <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
            ← Back to Templates
          </Button>
        )}
      </div>

      {/* Video preview */}
      {videoUrl && step < 3 && (
        <div className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden">
          <video src={videoUrl} className="w-full h-full object-contain" controls />
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
            {videoName || 'Selected Video'}
          </div>
        </div>
      )}

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && renderTemplateGrid()}
          {step === 2 && renderCustomizeStep()}
          {step === 3 && renderProcessingStep()}
        </motion.div>
      </AnimatePresence>

      {/* Next button for step 1 */}
      {step === 1 && selectedTemplate && (
        <Button
          onClick={() => setStep(2)}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          Continue with {selectedTemplate.name}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
}
